import { useSelector } from "react-redux";
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import { RootState } from "../../../redux/store";
import { menuRoutes } from "../../../router/menu";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useCallback, useEffect } from "react";
import useFetch from "../../../hooks/useFetch";
import { toast } from "sonner";
import PageHeader from "../../../layout/shared/page-header";
import DeletePopover from "../../../components/buttons/DeletePopover";
import { Button, Loader, Tooltip } from "rizzui";
import { setSelectedView } from "../../../redux/censusProfileSlice";
import StatusDropdown from "../../../components/forms/StatusDropdown";
import { CensusService } from "../../../services/census/censusService";
import useCensusStatus from "../../../hooks/api-calls/useCensusStatus";

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.ofc.profile.info,
        permission: {
            group: 'census',
            action: 'get',
        }
    },
    {
        label: 'Fotografias',
        path: menuRoutes.ofc.profile.photos,
        permission: {
            group: 'census_photos',
            action: 'get',
        }
    }

];


export default function OfcProfileLayout({ children, isLoading, setRefetchTab }: { children?: React.ReactNode, isLoading?: boolean, setRefetchTab?: (state:boolean) => void }) {

    // HOOKS

    const { selectedView } = useSelector((state: RootState) => state.censusProfile);
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const { getCensusStatusList } = useCensusStatus();
    const navigate = useNavigate();
    const service = new CensusService();


    // DATA FETCHING
    
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getCensusById(id);
        return response.getResponseData();
    }, [id]));

    const pageHeader = {
        title: `OFC ${data?.censusId || ''}`,
        breadcrumb: [
            { name: 'Administración' },
            { name: 'OFC' },
            { name: 'Datos del OFC' },
        ],
    }


    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR OFC
     * @EN DELETE OFC
     * @param id
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteCensus(id)).getResponseData();
            toast.success(response.message);
            navigate(menuRoutes.ofc.path);
        } catch (error) {
            handleErrors(error);
        }
    }
    //-----------------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR DE ESTADO EL OFC
     * @EN CHANGE THE STATUS OF THE OFC
     * 
     * @param id
     * @param status
     */ 
    //-----------------------------------------------------------------------------------------------------------------------
    const handleChangeStatus = async (id: string, status: string) => {
        const response = await (await service.changeStatusCensus(id, status)).getResponseData();
        if(response.success) {
            toast.success("Estado del OFC cambiado correctamente");
            navigate(menuRoutes.ofc.path);
        }else {
            handleErrors(response);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        navigate(`${menuRoutes.ofc.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);

    // RENDER

    return (
            <>
                <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb} className="mb-0 lg:mb-0">
                    <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                        { userCan('edit', 'census')&& data && (
                            <>
                                <div className='flex flex-col gap-2 justify-content-center text-center mt-1' key={id + data.status?.id}>
                                        {(id && data.status?.id) ? (
                                            <StatusDropdown
                                                entityId={id}
                                                title="¿Estas seguro de cambiar el estado del OFC?"
                                                statesOptions={getCensusStatusList()}
                                                currentState={data.status}
                                                handleStateChange={(entityId: string, statusId: string) => handleChangeStatus(id, statusId)}
                                                hasComments={false}
                                            />
                                        ) : "N/A"}
                                </div>
                                <Tooltip content={'Editar OFC'} placement={'top'} color={'invert'} size={'sm'}>
                                    <Button variant={'outline'} color={'primary'} size={'md'} onClick={() => {
                                        navigate(`${menuRoutes.ofc.path}/${id}/edit`);
                                    }} className="bg-white">
                                        Editar
                                    </Button>
                                </Tooltip>
                             </>
                        )}
                        {userCan('delete', 'census') && (
                            <DeletePopover
                                title={`Eliminar OFC`}
                                description={`¿Estás seguro de que deseas eliminar el OFC?`}
                                size={'md'}
                                onDelete={() => handleDelete(id)}
                                actionIconClassName='h-10 w-10'
                            />
                        )}

                    </div>
                </PageHeader>
    
                <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />
    
                {isLoading ? <Loader height='60vh' /> : < Outlet />}
            </>
        );
}